import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Eye, EyeOff, Headset, LoaderCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import EmployeeSelection from './EmployeeSelection';
import LoginLogoMark from './LoginLogoSculpture';
import { SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY } from './SupportContact';
import './Login.css';

const REMEMBERED_USERNAME_KEY = 'xentrix_login_username';

const readRememberedUsername = () => {
  try {
    return localStorage.getItem(REMEMBERED_USERNAME_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Σύνδεση καταστήματος (όνομα χρήστη + κωδικός) ή χρήστη οργανισμού (email + κωδικός) — το ίδιο
 * POST /api/auth/store-login τα ξεχωρίζει. Ο χρήστης οργανισμού μπαίνει κατευθείαν· το κατάστημα
 * ανοίγει την επιλογή υπαλλήλου και το PIN (EmployeeSelection → PinEntry).
 *
 * Νέα εμφάνιση (14/10/2026): μία ήσυχη στήλη πάνω στο «Χαρτί»/«Γραφίτη» του θέματος, ένα χρώμα
 * brand, χωρίς την εισαγωγική κίνηση· η φόρμα είναι ενεργή από την πρώτη στιγμή. Η λογική έμεινε ίδια.
 */
const Login: React.FC = () => {
  const { storeLogin, storeSession, employeeLogin, logout } = useAuth();
  const { t } = useTranslation('auth');
  const [username, setUsername] = useState(readRememberedUsername);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberUsername, setRememberUsername] = useState(() => Boolean(readRememberedUsername()));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError('');

    try {
      const result = await storeLogin(username, password);
      try {
        if (rememberUsername) localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
        else localStorage.removeItem(REMEMBERED_USERNAME_KEY);
      } catch {
        // Remembering a username is optional when browser storage is unavailable.
      }
      if (result.loginType === 'org_user') {
        window.location.href = '/';
        return;
      }
      // A store session opens employee selection below.
    } catch (err: any) {
      const raw = String(err.message || '');
      setError(/incorrect|invalid|failed/i.test(raw) || !raw ? t('loginFailed') : raw);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmployeeLogin = (data: any) => {
    employeeLogin(data);
    const role = data.employee?.role;
    if (role === 'kiosk' || role === 'super_admin' || role === 'store_admin' || role === 'support' || role === 'sales') {
      window.location.href = '/';
    } else {
      window.location.href = '/takeaway';
    }
  };

  if (storeSession) {
    return (
      <EmployeeSelection
        storeToken={storeSession.storeToken}
        store={storeSession.store}
        onEmployeeLogin={handleEmployeeLogin}
        onLogout={() => logout()}
      />
    );
  }

  return (
    <div className="xl-page">
      <header className="xl-bar">
        <a className="xl-brand" href="/" aria-label={`Xentrix Flow — ${t('home')}`}>
          <LoginLogoMark />
          <span>Xentrix Flow</span>
        </a>
        <a className="xl-bar-link" href={`tel:${SUPPORT_PHONE}`}>
          <Headset aria-hidden="true" />
          <span className="xl-hide-narrow">{t('redesign.help')}</span>
          <strong>{SUPPORT_PHONE_DISPLAY}</strong>
        </a>
      </header>

      <main className="xl-main">
        <section className="xl-login" aria-labelledby="flow-login-title">
          <div className="xl-login-head">
            <p className="xl-eyebrow">{t('redesign.eyebrow')}</p>
            <h1 id="flow-login-title" className="xl-title">{t('redesign.experienceTitle')} {t('redesign.experienceAccent')}</h1>
            <p className="xl-sub">{t('redesign.credentialsHint')}</p>
          </div>
          <form className="xl-form" onSubmit={handleSubmit} aria-busy={isLoading}>
            {error && (
              <div id="flow-login-error" className="xl-error" role="alert">
                <CircleAlert aria-hidden="true" /><span>{error}</span>
              </div>
            )}
            <div className="xl-field">
              <label htmlFor="username">{t('username')}</label>
              <input id="username" name="username" type="text" className="xl-input" placeholder={t('usernamePlaceholder')} autoComplete="username" autoCapitalize="none" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value)} disabled={isLoading} aria-describedby={error ? 'flow-login-error' : undefined} required />
            </div>
            <div className="xl-field">
              <div className="xl-label-row">
                <label htmlFor="password">{t('password')}</label>
                <a href="/contact">{t('forgotPassword')}</a>
              </div>
              <div className="xl-input-wrap">
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} className="xl-input" placeholder={t('passwordPlaceholder')} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={isLoading} aria-describedby={error ? 'flow-login-error' : undefined} required />
                <button type="button" className="xl-eye" onClick={() => setShowPassword((visible) => !visible)} aria-label={t(showPassword ? 'redesign.hidePassword' : 'redesign.showPassword')} aria-pressed={showPassword} aria-controls="password">
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </div>
            </div>
            <label className="xl-check" htmlFor="remember">
              <input id="remember" type="checkbox" checked={rememberUsername} disabled={isLoading} onChange={(e) => {
                setRememberUsername(e.target.checked);
                if (!e.target.checked) {
                  try { localStorage.removeItem(REMEMBERED_USERNAME_KEY); } catch { /* Storage is optional. */ }
                }
              }} />
              <span>{t('redesign.rememberUsername')}</span>
            </label>
            <button className="xl-primary" type="submit" disabled={isLoading}>
              {isLoading && <LoaderCircle className="xl-spin" aria-hidden="true" />}
              <span aria-live="polite">{isLoading ? t('loggingIn') : t('redesign.submit')}</span>
            </button>
          </form>
          <p className="xl-after">
            <span>{t('noAccount')}</span>
            <a href="/contact">{t('redesign.letsTalk')}</a>
          </p>
        </section>
      </main>

      <footer className="xl-foot">
        <span>© {new Date().getFullYear()} Xentrix Flow</span>
      </footer>
    </div>
  );
};

export default Login;
